/**
 * @fileoverview Locale-aware URL building and navigation utilities
 *
 * This module provides functions for building and navigating to locale-aware URLs,
 * handling BCP 47 locale codes, script subtag removal, and case formatting.
 *
 * @see {@link ../docs/locale-navigation-api.md} for comprehensive documentation and examples
 *
 * @module localeNavigation
 */

import logger from './logger';
import NavigationService from './NavigationService.js';
import {
    generateLocalePath,
    localeToLocalePath,
    extractRawLocaleStringFromPath,
    getCurrentLocaleCode
} from './utils/localeUtils';

// =============================================================================
// URL VALIDATION UTILITIES
// =============================================================================

/**
 * Check if a string represents a full URL (http/https)
 */
function isFullUrl(urlOrPath) {
    return urlOrPath && (urlOrPath.startsWith('http://') || urlOrPath.startsWith('https://'));
}

/**
 * Check if a string represents a protocol-relative URL (//)
 */
function isProtocolRelativeUrl(urlOrPath) {
    return urlOrPath && urlOrPath.startsWith('//');
}

/**
 * Check if a string requires URL parsing (full or protocol-relative)
 */
function requiresUrlParsing(urlOrPath) {
    return isFullUrl(urlOrPath) || isProtocolRelativeUrl(urlOrPath);
}

/**
 * Convert protocol-relative URL to absolute for parsing
 */
function ensureAbsoluteUrl(url) {
    return url.startsWith('//') ? `https:${url}` : url;
}

/**
 * Restore protocol-relative format if original was protocol-relative
 */
function restoreProtocolRelative(url, wasProtocolRelative) {
    return wasProtocolRelative ? url.replace(/^https:/, '') : url;
}

/**
 * Safely parse a URL with error handling
 */
function parseUrlSafely(urlString) {
    try {
        return new URL(urlString);
    } catch (error) {
        logger.warn('Invalid URL format:', urlString, error);
        return null;
    }
}

// =============================================================================
// LOCALE PATH UTILITIES
// =============================================================================

/**
 * Remove existing locale from a path to prevent duplication
 */
function cleanPathFromLocale(path) {
    const sourceLocalePath = extractRawLocaleStringFromPath(path);
    if (!sourceLocalePath) {
        return path;
    }
    return path.replace(/^\/([a-z]{2}-[A-Z]{2}|[a-z]{2}-[a-z]{2})/, '');
}

/**
 * Ensure path starts with forward slash
 */
function normalizePath(path) {
    return path.startsWith('/') ? path : `/${path}`;
}

/**
 * Build a locale-aware path from target locale and clean path
 */
function buildLocaleAwarePath(targetLocale, cleanPath, sourceLocalePath = null, caseFormat = 'preserve') {
    // If no sourceLocalePath provided, use current page's locale path for case pattern
    const fallbackLocalePath = sourceLocalePath || extractRawLocaleStringFromPath(NavigationService.getPathname());
    const targetLocalePath = localeToLocalePath(targetLocale, fallbackLocalePath, caseFormat);

    // If still no case pattern available, default to lowercase for URLs (unless overridden)
    const finalLocalePath = fallbackLocalePath ? targetLocalePath : targetLocalePath.toLowerCase();

    const normalizedPath = normalizePath(cleanPath);
    return `/${finalLocalePath}${normalizedPath}`;
}

/**
 * Determine the target locale from explicit locale or fallback to current
 */
function determineTargetLocale(explicitLocale) {
    return explicitLocale || getCurrentLocaleCode();
}

/**
 * Build URL from a full URL with locale handling
 */
function buildUrlFromFullUrl(urlOrPath, targetLocale, caseFormat = 'preserve') {
    const wasProtocolRelative = isProtocolRelativeUrl(urlOrPath);
    const urlToUse = ensureAbsoluteUrl(urlOrPath);
    const url = parseUrlSafely(urlToUse);

    if (!url) {
        return urlOrPath;
    }

    // If caseFormat is not 'preserve', skip generateLocalePath and use manual building
    // to ensure caseFormat is applied
    if (caseFormat !== 'preserve') {
        const sourceLocalePath = extractRawLocaleStringFromPath(url.pathname);
        const cleanPath = cleanPathFromLocale(url.pathname);
        const newPath = buildLocaleAwarePath(targetLocale, cleanPath, sourceLocalePath, caseFormat);
        url.pathname = newPath;
        const result = url.toString();
        return restoreProtocolRelative(result, wasProtocolRelative);
    }

    try {
        // Try to generate a new pathname with the target locale
        const newPathname = generateLocalePath(targetLocale, url.pathname);
        url.pathname = newPathname;
        const result = url.toString();
        return restoreProtocolRelative(result, wasProtocolRelative);
    } catch (error) {
        // If generateLocalePath fails, build path manually
        const sourceLocalePath = extractRawLocaleStringFromPath(url.pathname);
        const cleanPath = cleanPathFromLocale(url.pathname);
        const newPath = buildLocaleAwarePath(targetLocale, cleanPath, sourceLocalePath, caseFormat);
        url.pathname = newPath;
        const result = url.toString();
        return restoreProtocolRelative(result, wasProtocolRelative);
    }
}

/**
 * Build URL from a path with locale handling
 */
function buildUrlFromPath(urlOrPath, targetLocale, baseUrl = null, caseFormat = 'preserve') {
    // If caseFormat is not 'preserve', skip generateLocalePath and use manual building
    // to ensure caseFormat is applied
    if (caseFormat !== 'preserve') {
        const sourceLocalePath = extractRawLocaleStringFromPath(urlOrPath);
        const cleanPath = cleanPathFromLocale(urlOrPath);
        const newPath = buildLocaleAwarePath(targetLocale, cleanPath, sourceLocalePath, caseFormat);
        return baseUrl ? new URL(newPath, baseUrl).toString() : newPath;
    }

    try {
        const newPath = generateLocalePath(targetLocale, urlOrPath);
        return baseUrl ? new URL(newPath, baseUrl).toString() : newPath;
    } catch (error) {
        // If generateLocalePath fails, build path manually
        const sourceLocalePath = extractRawLocaleStringFromPath(urlOrPath);
        const cleanPath = cleanPathFromLocale(urlOrPath);
        const newPath = buildLocaleAwarePath(targetLocale, cleanPath, sourceLocalePath, caseFormat);
        return baseUrl ? new URL(newPath, baseUrl).toString() : newPath;
    }
}

// =============================================================================
// URL BUILDING UTILITIES
// =============================================================================

/**
 * Build a URL with the specified locale, handling both paths and full URLs
 *
 * Supports two signatures:
 * 1. Old: buildUrl(urlOrPath, localeCode, baseUrl)
 * 2. New: buildUrl(urlOrPath, { localeCode, baseUrl, caseFormat })
 *
 * @param {string} urlOrPath - The URL or path to build from
 * @param {string|Object} [localeCodeOrOptions] - The target BCP 47 locale code OR options object
 * @param {string} [localeCodeOrOptions.localeCode] - The target locale code (when using options object)
 * @param {string} [localeCodeOrOptions.baseUrl] - Base URL for path inputs (when using options object)
 * @param {string} [localeCodeOrOptions.caseFormat] - Case format: 'preserve', 'lowercase', or 'uppercase' (when using options object)
 * @param {string} [baseUrl] - Base URL for path inputs (when using old signature)
 * @returns {string} The built URL with proper locale
 */
export function buildUrl(urlOrPath, localeCodeOrOptions = null, baseUrl = null) {
    if (!urlOrPath || typeof urlOrPath !== 'string') {
        logger.warn('buildUrl: Invalid URL or path provided:', urlOrPath);
        return urlOrPath;
    }

    // Detect if using new options object signature or old positional parameters
    let targetLocale, targetBaseUrl, caseFormat;

    if (localeCodeOrOptions && typeof localeCodeOrOptions === 'object') {
        // New signature: options object
        targetLocale = determineTargetLocale(localeCodeOrOptions.localeCode);
        targetBaseUrl = localeCodeOrOptions.baseUrl || null;
        caseFormat = localeCodeOrOptions.caseFormat || 'preserve';
    } else {
        // Old signature: positional parameters
        targetLocale = determineTargetLocale(localeCodeOrOptions);
        targetBaseUrl = baseUrl;
        caseFormat = 'preserve';
    }

    if (requiresUrlParsing(urlOrPath)) {
        return buildUrlFromFullUrl(urlOrPath, targetLocale, caseFormat);
    }

    return buildUrlFromPath(urlOrPath, targetLocale, targetBaseUrl, caseFormat);
}

/**
 * Build a URL from a specific base path to a target path with locale
 * This preserves locale context from the source path
 *
 * Supports two signatures:
 * 1. Old: buildUrlFrom(fromPath, toPath, localeCode)
 * 2. New: buildUrlFrom(fromPath, toPath, { localeCode, caseFormat })
 *
 * @param {string} fromPath - The base path to extract locale context from
 * @param {string} toPath - The target path to build
 * @param {string|Object} [localeCodeOrOptions] - The target BCP 47 locale code OR options object
 * @param {string} [localeCodeOrOptions.localeCode] - The target locale code (when using options object)
 * @param {string} [localeCodeOrOptions.caseFormat] - Case format: 'preserve', 'lowercase', or 'uppercase' (when using options object)
 * @returns {string|null} The built URL or null if no locale can be determined
 */
export function buildUrlFrom(fromPath, toPath, localeCodeOrOptions = null) {
    const fromPathLocalePath = extractRawLocaleStringFromPath(fromPath);

    // Detect if using new options object signature or old positional parameters
    let targetLocale, caseFormat;

    if (localeCodeOrOptions && typeof localeCodeOrOptions === 'object') {
        // New signature: options object
        targetLocale = localeCodeOrOptions.localeCode || null;
        caseFormat = localeCodeOrOptions.caseFormat || 'preserve';
    } else {
        // Old signature: positional parameters
        targetLocale = localeCodeOrOptions;
        caseFormat = 'preserve';
    }

    if (!fromPathLocalePath && !targetLocale) {
        return null;
    }

    const finalTargetLocale = targetLocale || fromPathLocalePath;
    const cleanPath = cleanPathFromLocale(toPath);

    return buildLocaleAwarePath(finalTargetLocale, cleanPath, fromPathLocalePath, caseFormat);
}

// =============================================================================
// NAVIGATION UTILITIES
// =============================================================================

/**
 * Navigation builder class for fluent API
 */
class NavigationBuilder {
    constructor(pathOrUrl = null, localeCode = null) {
        this.sourcePath = pathOrUrl; // Keep original path for context
        this.targetPath = pathOrUrl;
        this.targetLocale = localeCode;
        this.isExternal = pathOrUrl && (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://'));
    }

    /**
     * Set the target path or URL
     * @param {string} pathOrUrl - The target path or URL
     * @returns {NavigationBuilder} This builder for chaining
     */
    to(pathOrUrl) {
        this.targetPath = pathOrUrl;
        this.isExternal = pathOrUrl && (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://'));
        return this;
    }

    /**
     * Set the target locale
     * @param {string} localeCode - The target locale code
     * @returns {NavigationBuilder} This builder for chaining
     */
    withLocale(localeCode) {
        this.targetLocale = localeCode;
        return this;
    }

    /**
     * Execute the navigation
     */
    go() {
        if (!this.targetPath) {
            logger.warn('NavigationBuilder: No target path specified');
            return;
        }

        const builtUrl = this._buildUrl();

        if (builtUrl) {
            NavigationService.assignAndNavigate(builtUrl);
        }
    }

    /**
     * Get the built URL without navigating
     * @returns {string} The built URL
     */
    getUrl() {
        if (!this.targetPath) {
            logger.warn('NavigationBuilder: No target path specified');
            return '';
        }
        return this._buildUrl();
    }

    /**
     * Internal method to build URL with proper context preservation
     * @returns {string} The built URL
     * @private
     */
    _buildUrl() {
        // If we have a source path and target locale, use buildUrlFrom to preserve context
        if (this.sourcePath && this.targetLocale) {
            return buildUrlFrom(this.sourcePath, this.targetPath, this.targetLocale);
        }

        // Otherwise use regular buildUrl
        return buildUrl(this.targetPath, this.targetLocale);
    }
}

/**
 * Create a new navigation builder
 * @param {string} [pathOrUrl] - Optional initial path or URL
 * @param {string} [localeCode] - Optional initial locale code
 * @returns {NavigationBuilder} A new navigation builder instance
 */
export function navigate(pathOrUrl = null, localeCode = null) {
    return new NavigationBuilder(pathOrUrl, localeCode);
}

/**
 * Navigate to a path or URL with optional locale change
 * @param {string} pathOrUrl - The target path or URL
 * @param {string} [localeCode] - The target locale code (defaults to current locale)
 */
export function navigateTo(pathOrUrl, localeCode = null) {
    const builtUrl = buildUrl(pathOrUrl, localeCode);

    if (builtUrl) {
        NavigationService.assignAndNavigate(builtUrl);
    }
}

/**
 * Switch to a different locale while keeping the same path
 * @param {string} localeCode - The target locale code
 */
export function switchToLocale(localeCode) {
    const currentPath = NavigationService.getPathname();
    navigateTo(currentPath, localeCode);
}

// =============================================================================
// MODULE EXPORTS
// =============================================================================

export default {
    buildUrl,
    buildUrlFrom,
    navigate,
    navigateTo,
    switchToLocale,
};
